import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as styles from '@common/components/Modal/Modal.module.css';

export default function Modal({
  title,
  subtitle,
  body,
  children,
  submitText,
  submitAction,
  submitDisabled = false,
  submitVariant = 'primary',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  hideFooter = false,
  maxWidth,
  onClose,
  isOpen = true,
  className,
  cancelText,
  headerIcon,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen || !closeOnEscape) {
      return undefined;
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, closeOnEscape]);

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not its children
    if (e.target === e.currentTarget && onClose && closeOnOverlayClick) {
      onClose();
    }
  };

  const handleSubmit = () => {
    if (submitAction) {
      submitAction();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        className={`${styles.modal} ${className || ''}`}
        style={maxWidth ? { maxWidth } : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby={subtitle ? 'modal-subtitle' : undefined}
      >
        {/* Header */}
        {title && (
          <div className={styles.header}>
            <div className={styles.headerContent}>
              {headerIcon}
              <h2 id="modal-title" className={styles.title}>
                {title}
              </h2>
              {subtitle && (
                <p id="modal-subtitle" className={styles.subtitle}>
                  {subtitle}
                </p>
              )}
            </div>
            {showCloseButton && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close modal"
              >
                ×
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={styles.body}>{children || body}</div>

        {/* Footer */}
        {!hideFooter && (
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
            >
              {cancelText || t('common.close')}
            </button>
            {submitText && (
              <button
                type="button"
                className={submitVariant === 'danger' ? styles.submitButtonDanger : styles.submitButton}
                onClick={handleSubmit}
                disabled={submitDisabled}
              >
                {submitText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
